import React, { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Skeleton,
  TextField,
  Typography,
} from "@mui/material";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";

import {
  IPD_COLORS,
  IPD_SHAPE,
  getIpdSelectMenuProps,
  ipdElevatedInputSx,
  ipdLabelSx,
  IpdButton,
} from "../../ipd/utils";
import { changeEmergencyProvider, getAssignable } from "../api/emergencyApi";
import { truncatedSelectSx } from "../constants";

/**
 * Hand over the Emergency Provider (ER-PRV-003).
 *
 * Clinical responsibility passes to a different doctor at a shift change, or
 * when a case needs someone else. Unlike assigning a bed, the patient keeps
 * their bed here — only the doctor changes — so this is deliberately a separate
 * action from the bed dialog rather than a side effect of moving beds.
 *
 * The doctor currently responsible is left out of the list: handing a visit to
 * the doctor who already holds it records nothing, so it is not offered.
 */
export default function ProviderHandoverDialog({ open, visit, onClose, onHandedOver }) {
  const [doctors, setDoctors] = useState(null);
  const [loading, setLoading] = useState(false);
  const [providerId, setProviderId] = useState("");
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      // The assignable endpoint already returns the facility's doctors; the
      // free beds it also carries are simply unused here.
      const data = await getAssignable();
      setDoctors(data?.doctors || []);
    } catch (e) {
      setError(e?.message || "Could not load the doctors");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!open) return;
    setProviderId("");
    setReason("");
    setError("");
    load();
  }, [open, load]);

  const submit = async () => {
    if (!providerId) return;
    setSaving(true);
    setError("");
    try {
      const { message } = await changeEmergencyProvider(visit.id, { providerId, reason });
      onHandedOver?.(message);
      onClose();
    } catch (e) {
      setError(e?.message || "Could not hand over the Emergency Provider");
    } finally {
      setSaving(false);
    }
  };

  if (!visit) return null;

  // The current provider cannot receive the handover — offering them would only
  // produce a refused submission.
  const options = (doctors || []).filter((d) => d.id !== visit.provider_id);
  const noneToHandTo = !loading && options.length === 0;

  return (
    <Dialog
      open={open}
      onClose={saving ? undefined : onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{ sx: { borderRadius: `${IPD_SHAPE.radiusLg}px` } }}
    >
      <DialogTitle sx={{ pb: 0.5 }}>
        <Typography sx={{ fontSize: 17, fontWeight: 700, color: IPD_COLORS.textPrimary }}>
          Hand over the Emergency Provider
        </Typography>
        <Typography sx={{ fontSize: 13, color: IPD_COLORS.textSecondary, mt: 0.25 }}>
          {visit.provider_name
            ? `${visit.provider_name} holds this visit now. The patient keeps their bed — only the responsible doctor changes.`
            : "The patient keeps their bed — only the responsible doctor changes."}
        </Typography>
      </DialogTitle>

      <DialogContent sx={{ pt: 2 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2, borderRadius: `${IPD_SHAPE.radiusMd}px` }}>
            {error}
          </Alert>
        )}

        {loading ? (
          <Box sx={{ display: "grid", gap: 2 }}>
            <Skeleton variant="rounded" height={44} />
            <Skeleton variant="rounded" height={72} />
          </Box>
        ) : noneToHandTo ? (
          <Box
            sx={{
              textAlign: "center",
              py: 3,
              px: 2,
              border: `1px dashed ${IPD_COLORS.borderHover}`,
              borderRadius: `${IPD_SHAPE.radiusMd}px`,
            }}
          >
            <SwapHorizIcon sx={{ fontSize: 26, color: IPD_COLORS.textMuted }} />
            <Typography sx={{ mt: 1, fontSize: 14, fontWeight: 700 }}>
              No other doctor to hand over to
            </Typography>
            <Typography sx={{ mt: 0.5, fontSize: 13, color: IPD_COLORS.textSecondary }}>
              This facility has no doctor other than the current provider on record.
            </Typography>
          </Box>
        ) : (
          // minmax(0, 1fr) rather than a bare 1fr: a grid item defaults to
          // min-width auto and so refuses to shrink below its content, which
          // let a long doctor name push the select wider than the dialog and
          // scroll the whole thing sideways.
          <Box sx={{ display: "grid", gap: 2, gridTemplateColumns: "minmax(0, 1fr)" }}>
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{ ...ipdLabelSx, mb: 0.75 }}>
                Hand over to <span style={{ color: IPD_COLORS.error.text }}>*</span>
              </Typography>
              <TextField
                select
                value={providerId}
                onChange={(e) => setProviderId(e.target.value)}
                size="small"
                fullWidth
                sx={{ ...ipdElevatedInputSx, ...truncatedSelectSx }}
                SelectProps={{ displayEmpty: true, MenuProps: getIpdSelectMenuProps() }}
                inputProps={{ "aria-label": "New Emergency Provider" }}
                helperText="Takes over clinical responsibility for this visit."
              >
                <MenuItem value="" disabled>
                  Name the doctor taking over
                </MenuItem>
                {options.map((d) => (
                  <MenuItem key={d.id} value={d.id}>
                    {d.name}{d.specialization ? ` — ${d.specialization}` : ""}
                  </MenuItem>
                ))}
              </TextField>
            </Box>

            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{ ...ipdLabelSx, mb: 0.75 }}>Reason (optional)</Typography>
              <TextField
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                size="small"
                fullWidth
                multiline
                minRows={2}
                placeholder="Shift change, referred to specialty, etc."
                sx={ipdElevatedInputSx}
                inputProps={{ "aria-label": "Reason for handover" }}
              />
            </Box>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <IpdButton variant="outlined" onClick={onClose} disabled={saving}>
          Cancel
        </IpdButton>
        <IpdButton onClick={submit} loading={saving} disabled={!providerId || noneToHandTo}>
          Hand over
        </IpdButton>
      </DialogActions>
    </Dialog>
  );
}
